import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Camera, RotateCcw, Settings, Info, BarChart2, Loader } from 'lucide-react';
import '../styles/DotCounter.css';
import '../styles/progressBar.css';
import '../styles/RobustAnalysis.css';
import { calculateRobustEstimate, sweepParameters, multiScaleDetection } from '../utils/robustCounting';
import createRobustCounterWorkerManager from '../utils/workerManager';

interface DotCounterProps {
  // Add any props if needed
}

interface DetectionSettings {
  minArea: number;
  maxArea: number;
  threshold: number;
  saturationMin: number;
  showBounds: boolean;
  minDistance: number;
}

interface ComponentData {
  area: number;
  centerX: number;
  centerY: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  pixels: { x: number; y: number }[];
}

interface Result {
  totalComponents?: number;
  validComponents?: number;
  filteredComponents?: ComponentData[];
  brightPixels?: number;
  imageSize?: { width: number; height: number };
  error?: string;
}

interface RobustEstimation {
  estimatedCount: number;
  lowerBound: number;
  upperBound: number;
  confidence: number;
  stableRegionSize: number;
  totalTrials: number;
  isProcessing: boolean;
  progress: number;
}

const DotCounter: React.FC<DotCounterProps> = () => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [settings, setSettings] = useState<DetectionSettings>({
    minArea: 1,
    maxArea: 35,
    threshold: 150,
    saturationMin: 50,
    showBounds: true,
    minDistance: 15 // Default minimum distance between dots (in pixels)
  });
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [robustEstimation, setRobustEstimation] = useState<RobustEstimation>({
    estimatedCount: 0,
    lowerBound: 0,
    upperBound: 0,
    confidence: 0,
    stableRegionSize: 0,
    totalTrials: 0,
    isProcessing: false,
    progress: 0
  });
  const [showRobustAnalysis, setShowRobustAnalysis] = useState<boolean>(false);
  
  // Area restriction state
  const [restrictionPolygon, setRestrictionPolygon] = useState<{x: number, y: number}[]>([]);
  const [isDrawingRestriction, setIsDrawingRestriction] = useState<boolean>(false);
  const [showRestrictionArea, setShowRestrictionArea] = useState<boolean>(false);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [lassoOpacity, setLassoOpacity] = useState(0.3); // For fade-out
  const lassoFadeTimeout = useRef<number | null>(null);
  const lassoFadeAnimFrame = useRef<number | null>(null);
  
  // Temporary drawing path for real-time feedback
  const currentDrawingPath = useRef<{x: number, y: number}[]>([]);
  const lastUpdateTime = useRef<number>(0);
  const animationFrameId = useRef<number | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // Create a reference to store our worker manager instance
  const workerManagerRef = useRef<ReturnType<typeof createRobustCounterWorkerManager> | null>(null);
  
  // Initialize the worker manager when component mounts
  useEffect(() => {
    workerManagerRef.current = createRobustCounterWorkerManager();
    
    // Cleanup when component unmounts
    return () => {
      if (workerManagerRef.current) {
        workerManagerRef.current.terminateWorker();
      }
      // Cancel any pending animation frames
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [workerManagerRef]);



  // Utility function to check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = (x: number, y: number, polygon: {x: number, y: number}[]): boolean => {
    if (polygon.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Get mouse position relative to canvas
  const getCanvasMousePos = (e: React.MouseEvent<HTMLCanvasElement>): {x: number, y: number} => {
    const canvas = canvasRef.current;
    if (!canvas) return {x: 0, y: 0};
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Draw real-time lasso feedback directly on canvas
  const drawRealtimeLasso = useCallback((path: {x: number, y: number}[], opacityOverride?: number) => {
    const canvas = canvasRef.current;
    if (!canvas || path.length < 2) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Save current canvas state
    ctx.save();
    
    // Draw the current lasso path
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.globalAlpha = 0.8;
    
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i].x, path[i].y);
    }
    
    ctx.stroke();
    
    // Draw semi-transparent fill if we have enough points
    if (path.length >= 3) {
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
      ctx.globalAlpha = opacityOverride !== undefined ? opacityOverride : lassoOpacity;
      
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) {
        ctx.lineTo(path[i].x, path[i].y);
      }
      ctx.closePath();
      ctx.fill();
    }
    
    // Draw start point
    ctx.setLineDash([]);
    ctx.fillStyle = '#3b82f6';
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(path[0].x, path[0].y, 4, 0, 2 * Math.PI);
    ctx.fill();
    
    // Restore canvas state
    ctx.restore();
  }, [lassoOpacity]);



  // Handle canvas mouse events for lasso drawing
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRestriction) return;
    
    setIsMouseDown(true);
    const pos = getCanvasMousePos(e);
    currentDrawingPath.current = [pos];
    setRestrictionPolygon([pos]); // Start new lasso path
  }, [isDrawingRestriction]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRestriction || !isMouseDown) return;
    
    const pos = getCanvasMousePos(e);
    const lastPoint = currentDrawingPath.current[currentDrawingPath.current.length - 1];
    
    if (!lastPoint) {
      currentDrawingPath.current = [pos];
      return;
    }
    
    const distance = Math.sqrt(
      Math.pow(pos.x - lastPoint.x, 2) + Math.pow(pos.y - lastPoint.y, 2)
    );
    
    // Add point if it's at least 2 pixels away from the last point
    if (distance >= 2) {
      currentDrawingPath.current.push(pos);
      
      // Cancel previous animation frame
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      
      // Schedule real-time drawing update
      animationFrameId.current = requestAnimationFrame(() => {
        drawRealtimeLasso(currentDrawingPath.current);
      });
      
      // Throttle state updates to prevent excessive re-renders
      const now = Date.now();
      if (now - lastUpdateTime.current > 100) { // Update state max every 100ms
        lastUpdateTime.current = now;
        setRestrictionPolygon([...currentDrawingPath.current]);
      }
    }
  }, [isDrawingRestriction, isMouseDown, drawRealtimeLasso]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRestriction || !isMouseDown) return;
    
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    
    setIsMouseDown(false);
    
    // Finish drawing the lasso if we have enough points
    if (currentDrawingPath.current.length >= 3) {
      setRestrictionPolygon([...currentDrawingPath.current]);
      setIsDrawingRestriction(false);
      setShowRestrictionArea(true);
      
      // Start fade-out animation after a brief delay
      if (lassoFadeTimeout.current) clearTimeout(lassoFadeTimeout.current);
      if (lassoFadeAnimFrame.current) cancelAnimationFrame(lassoFadeAnimFrame.current);
      lassoFadeTimeout.current = window.setTimeout(() => {
        let opacity = 0.3;
        const fadeStep = () => {
          opacity -= 0.02; // Smooth fade
          if (opacity <= 0.1) {
            setLassoOpacity(0.1); // Keep it slightly visible
            return;
          }
          setLassoOpacity(opacity);
          // Redraw the lasso with new opacity (this causes slight flicker but works)
          if (image && canvasRef.current) {
            processImage(image);
            setTimeout(() => drawRealtimeLasso(currentDrawingPath.current, opacity), 10);
          }
          // Use requestAnimationFrame for smooth animation
          lassoFadeAnimFrame.current = requestAnimationFrame(fadeStep);
        };
        fadeStep();
      }, 600); // Wait 600ms before starting fade-out
    }
  }, [isDrawingRestriction, isMouseDown, image, drawRealtimeLasso]);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRestriction || !isMouseDown) return;
    
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    
    setIsMouseDown(false);
    
    // Finish drawing the lasso if we have enough points
    if (currentDrawingPath.current.length >= 3) {
      setRestrictionPolygon([...currentDrawingPath.current]);
      setIsDrawingRestriction(false);
      setShowRestrictionArea(true);
    }
  }, [isDrawingRestriction, isMouseDown]);

  // Start drawing restriction area
  const startDrawingRestriction = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (lassoFadeTimeout.current) clearTimeout(lassoFadeTimeout.current);
    if (lassoFadeAnimFrame.current) cancelAnimationFrame(lassoFadeAnimFrame.current);
    
    setLassoOpacity(0.3); // Reset opacity
    currentDrawingPath.current = [];
    setRestrictionPolygon([]);
    setIsDrawingRestriction(true);
    setShowRestrictionArea(false);
    setIsMouseDown(false);
    
    // Clear any existing lasso overlay by redrawing the canvas
    if (image && canvasRef.current) {
      processImage(image);
    }
  }, []);

  // Clear restriction area
  const clearRestrictionArea = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (lassoFadeTimeout.current) clearTimeout(lassoFadeTimeout.current);
    if (lassoFadeAnimFrame.current) cancelAnimationFrame(lassoFadeAnimFrame.current);
    
    setLassoOpacity(0.3); // Reset opacity
    currentDrawingPath.current = [];
    setRestrictionPolygon([]);
    setIsDrawingRestriction(false);
    setShowRestrictionArea(false);
    setIsMouseDown(false);
    
    // Clear any visual overlay by triggering a reprocess if we have an image
    if (image && canvasRef.current) {
      // Force a reprocess to clear the canvas and redraw without restriction overlay
      setTimeout(() => {
        if (image) {
          processImage(image);
        }
      }, 0);
    }
  }, [image]);

  // Connected component labeling algorithm - extracted for reuse
  const connectedComponentLabeling = (mask: Uint8Array, width: number, height: number): [Uint32Array, number] => {
    // Initialize labels
    const labels = new Uint32Array(width * height);
    let nextLabel = 1;
    
    // First pass: assign initial labels and build equivalence classes
    const equivalences: number[][] = [];
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        
        // Skip if pixel is not part of a component
        if (mask[idx] === 0) {
          continue;
        }
        
        // Check neighbors (4-connectivity)
        const neighbors: number[] = [];
        
        // West
        if (x > 0 && mask[idx - 1] === 1) {
          neighbors.push(labels[idx - 1]);
        }
        
        // North
        if (y > 0 && mask[(y - 1) * width + x] === 1) {
          neighbors.push(labels[(y - 1) * width + x]);
        }
        
        if (neighbors.length === 0) {
          // No labeled neighbors, create new label
          labels[idx] = nextLabel;
          equivalences[nextLabel] = [nextLabel];
          nextLabel++;
        } else {
          // Use the minimum neighbor label
          const minLabel = Math.min(...neighbors.filter(n => n > 0));
          labels[idx] = minLabel;
          
          // Update equivalence classes
          for (const neighbor of neighbors) {
            if (neighbor > 0 && neighbor !== minLabel) {
              equivalences[neighbor].push(minLabel);
              equivalences[minLabel].push(neighbor);
            }
          }
        }
      }
    }
    
    // Flatten equivalence classes
    const rootLabels = new Uint32Array(nextLabel);
    for (let i = 0; i < nextLabel; i++) {
      rootLabels[i] = i;
    }
    
    // Find roots
    const findRoot = (label: number): number => {
      let root = label;
      while (rootLabels[root] !== root) {
        root = rootLabels[root];
      }
      return root;
    };
    
    // Merge equivalence classes
    for (let i = 1; i < nextLabel; i++) {
      if (equivalences[i]) {
        const root = findRoot(i);
        for (const equiv of equivalences[i]) {
          const equivRoot = findRoot(equiv);
          if (root !== equivRoot) {
            rootLabels[equivRoot] = root;
          }
        }
      }
    }
    
    // Compress paths for all labels
    for (let i = 1; i < nextLabel; i++) {
      rootLabels[i] = findRoot(i);
    }
    
    // Second pass: relabel components with root labels
    let finalNumComponents = 0;
    const finalLabels = new Map<number, number>();
    
    for (let i = 0; i < width * height; i++) {
      if (labels[i] > 0) {
        const root = rootLabels[labels[i]];
        if (!finalLabels.has(root)) {
          finalNumComponents++;
          finalLabels.set(root, finalNumComponents);
        }
        labels[i] = finalLabels.get(root) || 0;
      }
    }
    
    return [labels, finalNumComponents];
  };

  const processImage = useCallback(async (imageData: HTMLImageElement) => {
    setProcessing(true);
    console.log('Starting image processing...');
    
    try {
      console.log('Processing image:', imageData);
      
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('Canvas ref is null');
        throw new Error('Canvas not available');
      }
      console.log('Canvas element:', canvas);
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get 2D context');
        throw new Error('Canvas context not available');
      }
      console.log('Canvas context obtained');
      
      // Set canvas size to match image
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      console.log('Canvas size set to:', canvas.width, 'x', canvas.height);
      
      // Draw image to canvas
      ctx.drawImage(imageData, 0, 0);
      console.log('Image drawn to canvas');
      
      // Get image data
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;
      
      // Detect if this is a B&W image by analyzing saturation
      let totalSaturation = 0;
      let pixelCount = 0;
      
      // First pass: calculate average saturation
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;
        const saturation = max === 0 ? 0 : (delta / max) * 255;
        
        totalSaturation += saturation;
        pixelCount++;
      }
      
      const avgSaturation = totalSaturation / pixelCount;
      const isBlackAndWhite = avgSaturation < 50; // Detect B&W images
      
      console.log('ProcessImage: Detected', isBlackAndWhite ? 'B&W' : 'color', 'image with avg saturation:', avgSaturation);
      
      // Create mask using appropriate method based on image type
      const mask = new Uint8Array(canvas.width * canvas.height);
      let brightPixels = 0;
      
      if (isBlackAndWhite) {
        console.log('Using B&W mask approach for dot detection');
        
        // For B&W images: use brightness-only detection with dynamic threshold
        // Determine background type
        let darkPixels = 0;
        let brightPixelCount = 0;
        
        for (let i = 0; i < data.length; i += 4) {
          const brightness = Math.max(data[i], data[i + 1], data[i + 2]);
          if (brightness < 100) darkPixels++;
          if (brightness > 150) brightPixelCount++;
        }
        
        const isDarkBackground = darkPixels > brightPixelCount;
        console.log('B&W image has', isDarkBackground ? 'dark' : 'light', 'background');
        
        // Apply B&W mask logic
        for (let i = 0; i < data.length; i += 4) {
          const brightness = Math.max(data[i], data[i + 1], data[i + 2]);
          const pixelIndex = Math.floor(i / 4);
          
          if (isDarkBackground) {
            // Dark background: detect bright dots
            if (brightness > settings.threshold) {
              mask[pixelIndex] = 255;
              brightPixels++;
            } else {
              mask[pixelIndex] = 0;
            }
          } else {
            // Light background: detect dark dots (invert logic)
            if (brightness < (255 - settings.threshold)) {
              mask[pixelIndex] = 255;
              brightPixels++;
            } else {
              mask[pixelIndex] = 0;
            }
          }
        }
      } else {
        console.log('Using standard HSV approach for color image');
        
        // For color images: use standard HSV approach
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          // Convert RGB to HSV
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const delta = max - min;
          
          const v = max / 255; // Value (brightness)
          const s = max === 0 ? 0 : delta / max; // Saturation
          
          // Check if pixel meets criteria (bright and saturated)
          const pixelIndex = Math.floor(i / 4);
          if (v * 255 > settings.threshold && s * 255 > settings.saturationMin) {
            mask[pixelIndex] = 255;
            brightPixels++;
          } else {
            mask[pixelIndex] = 0;
          }
        }
      }
      
      // Simple connected component labeling
      const labels = new Int32Array(canvas.width * canvas.height);
      let currentLabel = 1;
      const components: ComponentData[] = [];
      
      // First pass - assign labels
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const idx = y * canvas.width + x;
          
          if (mask[idx] === 255 && labels[idx] === 0) {
            // Start flood fill
            const component: ComponentData = { 
              pixels: [], 
              minX: x, 
              maxX: x, 
              minY: y, 
              maxY: y,
              area: 0,  // Will be updated later
              centerX: 0, // Will be updated later
              centerY: 0  // Will be updated later
            };
            const stack = [{ x, y }];
            
            while (stack.length > 0) {
              const { x: cx, y: cy } = stack.pop()!;
              const cidx = cy * canvas.width + cx;
              
              if (cx < 0 || cx >= canvas.width || cy < 0 || cy >= canvas.height ||
                  mask[cidx] === 0 || labels[cidx] !== 0) {
                continue;
              }
              
              labels[cidx] = currentLabel;
              component.pixels.push({ x: cx, y: cy });
              component.minX = Math.min(component.minX, cx);
              component.maxX = Math.max(component.maxX, cx);
              component.minY = Math.min(component.minY, cy);
              component.maxY = Math.max(component.maxY, cy);
              
              // Add neighbors
              stack.push({ x: cx + 1, y: cy });
              stack.push({ x: cx - 1, y: cy });
              stack.push({ x: cx, y: cy + 1 });
              stack.push({ x: cx, y: cy - 1 });
            }
            
            if (component.pixels.length > 0) {
              component.area = component.pixels.length;
              component.centerX = component.pixels.reduce((sum, p) => sum + p.x, 0) / component.pixels.length;
              component.centerY = component.pixels.reduce((sum, p) => sum + p.y, 0) / component.pixels.length;
              components.push(component);
              currentLabel++;
            }
          }
        }
      }
      
      // Filter components by area and position
      let filteredComponents;
      
      if (isBlackAndWhite) {
        console.log('Using permissive filtering for B&W image');
        // For B&W images: use very permissive filtering
        filteredComponents = components.filter((comp) => {
          // Much more permissive area filtering for B&W images
          const isValidArea = comp.area >= 1 && comp.area <= 100; // Very wide range
          
          // Minimal edge filtering for B&W images
          const edgePadding = 2; // Reduced padding
          const isNotAtEdge = 
            comp.minX > edgePadding && 
            comp.minY > edgePadding && 
            comp.maxX < canvas.width - edgePadding && 
            comp.maxY < canvas.height - edgePadding;
          
          // Check if component is within restriction area (if defined)
          const isInRestrictionArea = restrictionPolygon.length < 3 || 
            isPointInPolygon(comp.centerX, comp.centerY, restrictionPolygon);
          
          return isValidArea && isNotAtEdge && isInRestrictionArea;
        });
      } else {
        console.log('Using standard filtering for color image');
        // For color images: use standard filtering
        filteredComponents = components.filter((comp) => {
          // Filter by area
          const isValidArea = comp.area >= settings.minArea && comp.area <= settings.maxArea;
          
          // Filter out components too close to the edge (which might be partial dots)
          const edgePadding = 5;
          const isNotAtEdge = 
            comp.minX > edgePadding && 
            comp.minY > edgePadding && 
            comp.maxX < canvas.width - edgePadding && 
            comp.maxY < canvas.height - edgePadding;
          
          // Check if component is within restriction area (if defined)
          const isInRestrictionArea = restrictionPolygon.length < 3 || 
            isPointInPolygon(comp.centerX, comp.centerY, restrictionPolygon);
          
          return isValidArea && isNotAtEdge && isInRestrictionArea;
        });
      }
      
      // Sort components by area (largest first) to prioritize larger dots when filtering by distance
      filteredComponents.sort((a, b) => b.area - a.area);
      
      // Filter out dots that are too close to each other
      const { minDistance } = settings;
      const effectiveMinDistance = isBlackAndWhite ? Math.min(minDistance, 3) : minDistance; // Reduce min distance for B&W
      
      console.log('Using min distance:', effectiveMinDistance, 'for', isBlackAndWhite ? 'B&W' : 'color', 'image');
      
      if (effectiveMinDistance > 0) {
        // Create a new array with only dots that maintain minimum distance
        const distanceFilteredComponents: ComponentData[] = [];
        
        // For each component, check if it's too close to any already-accepted component
        for (const comp of filteredComponents) {
          let isTooClose = false;
          
          // Check distance against all already accepted components
          for (const accepted of distanceFilteredComponents) {
            // Calculate distance between dot centers
            const dx = comp.centerX - accepted.centerX;
            const dy = comp.centerY - accepted.centerY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // If too close, mark for exclusion
            if (distance < effectiveMinDistance) {
              isTooClose = true;
              break;
            }
          }
          
          // If not too close to any accepted dot, add it to the filtered list
          if (!isTooClose) {
            distanceFilteredComponents.push(comp);
          }
        }
        
        // Replace the filtered components with the distance-filtered ones
        filteredComponents = distanceFilteredComponents;
      }
      
      // Draw results on canvas
      ctx.drawImage(imageData, 0, 0);
      console.log('Found', filteredComponents.length, 'valid components out of', components.length, 'total');
      
      if (settings.showBounds) {
        filteredComponents.forEach((comp) => {
          // Draw bounding box
          ctx.strokeStyle = '#00ff00';
          ctx.lineWidth = 1;
          ctx.strokeRect(comp.minX - 2, comp.minY - 2, 
                        comp.maxX - comp.minX + 4, comp.maxY - comp.minY + 4);
          
          // Draw center point
          ctx.fillStyle = '#ff0000';
          ctx.fillRect(comp.centerX! - 1, comp.centerY! - 1, 3, 3);
        });
      }
      
      // Draw restriction lasso if it exists
      if (restrictionPolygon.length > 0) {
        ctx.strokeStyle = isDrawingRestriction ? '#ff6b35' : '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        if (restrictionPolygon.length > 1) {
          ctx.beginPath();
          ctx.moveTo(restrictionPolygon[0].x, restrictionPolygon[0].y);
          
          // Draw smooth path through all points
          for (let i = 1; i < restrictionPolygon.length; i++) {
            ctx.lineTo(restrictionPolygon[i].x, restrictionPolygon[i].y);
          }
          
          // Close the lasso if not currently drawing
          if (!isDrawingRestriction && restrictionPolygon.length > 2) {
            ctx.closePath();
            
            // Fill the lasso area with a semi-transparent overlay
            ctx.setLineDash([]);
            ctx.fillStyle = isDrawingRestriction ? 'rgba(255, 107, 53, 0.1)' : 'rgba(59, 130, 246, 0.1)';
            ctx.fill();
            
            // Restore stroke settings for the border
            ctx.setLineDash([5, 5]);
          }
          
          ctx.stroke();
        }
        
        // Draw start point indicator when drawing
        if (isDrawingRestriction && restrictionPolygon.length > 0) {
          ctx.setLineDash([]);
          ctx.fillStyle = '#ff6b35';
          ctx.beginPath();
          ctx.arc(restrictionPolygon[0].x, restrictionPolygon[0].y, 4, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
      
      setResult({
        totalComponents: components.length,
        validComponents: filteredComponents.length,
        filteredComponents: filteredComponents,
        brightPixels,
        imageSize: { width: canvas.width, height: canvas.height }
      });
      
    } catch (error) {
      console.error('Error processing image:', error);
      // More detailed error message for debugging
      const errorMessage = error instanceof Error 
        ? `Failed to process image: ${error.message}` 
        : 'Failed to process image: Unknown error';
      
      console.error(errorMessage);
      setResult({ error: errorMessage });
    } finally {
      setProcessing(false);
    }
  }, [settings]);

  // This useEffect handles re-processing when settings or image changes
  useEffect(() => {
    // Only reprocess if we have an image and we're not currently processing
    if (image && !processing) {
      processImage(image);
    }
  }, [image, processImage, processing, settings]);

  // Function to extract optimal settings from robust estimation results
  const extractOptimalSettingsFromRobust = useCallback(async (img: HTMLImageElement): Promise<DetectionSettings> => {
    console.log('Extracting optimal settings from robust analysis...');
    
    // Mark as processing without showing the panel
    setRobustEstimation(prev => ({ ...prev, isProcessing: true }));
    
    try {
      // Process single image with different parameter settings
      const processWithParams = async (params: Record<string, number | boolean>) => {
        // Create a temporary canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        if (!tempCtx) {
          throw new Error('Could not get canvas context for robust estimation');
        }
        
        // Draw image
        tempCtx.drawImage(img, 0, 0);
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imageData.data;
        
        // Extract parameters
        const threshold = typeof params.threshold === 'number' ? params.threshold : settings.threshold;
        const saturationMin = typeof params.saturationMin === 'number' ? params.saturationMin : settings.saturationMin;
        const minArea = typeof params.minArea === 'number' ? params.minArea : settings.minArea;
        const maxArea = typeof params.maxArea === 'number' ? params.maxArea : settings.maxArea;
        const minDistance = typeof params.minDistance === 'number' ? params.minDistance : settings.minDistance;
        
        // Process pixels and apply mask (same code as in performRobustCountEstimation)
        // [Code simplified for brevity - identical to the code in performRobustCountEstimation]
        let brightPixelCount = 0;
        const width = tempCanvas.width;
        const height = tempCanvas.height;
        
        const mask = new Uint8Array(width * height);
        
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const delta = max - min;
          
          const brightness = max;
          const saturation = max === 0 ? 0 : Math.round((delta / max) * 255);
          
          const pixelIndex = (i / 4);
          if (brightness >= threshold && saturation >= saturationMin) {
            mask[pixelIndex] = 1;
            brightPixelCount++;
          } else {
            mask[pixelIndex] = 0;
          }
        }
        
        // Perform connected component labeling
        const [labels, numComponents] = connectedComponentLabeling(mask, width, height);
        
        // Extract component data
        const components: ComponentData[] = [];
        for (let i = 1; i <= numComponents; i++) {
          let minX = width;
          let minY = height;
          let maxX = 0;
          let maxY = 0;
          let count = 0;
          const pixels: { x: number; y: number }[] = [];
          
          // Find bounding box and pixels
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const idx = y * width + x;
              if (labels[idx] === i) {
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
                count++;
                pixels.push({ x, y });
              }
            }
          }
          
          // Calculate center
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          
          components.push({
            area: count,
            centerX,
            centerY,
            minX,
            maxX,
            minY,
            maxY,
            pixels
          });
        }
        
        // Filter components by area
        let filteredComponents = components.filter(comp => 
          comp.area >= minArea && comp.area <= maxArea
        );
        
        // Filter out components near edges if they're small
        filteredComponents = filteredComponents.filter(comp => {
          const isTouchingEdge = 
            comp.minX <= 1 || comp.maxX >= width - 2 || 
            comp.minY <= 1 || comp.maxY >= height - 2;
          
          return !(comp.area < maxArea * 0.5 && isTouchingEdge);
        });
        
        // Filter by restriction area if defined
        if (restrictionPolygon.length >= 3) {
          filteredComponents = filteredComponents.filter(comp => 
            isPointInPolygon(comp.centerX, comp.centerY, restrictionPolygon)
          );
        }
        
        // Filter by minimum distance
        if (typeof minDistance === 'number' && minDistance > 0) {
          filteredComponents.sort((a, b) => b.area - a.area);
          
          const acceptedComponents: ComponentData[] = [];
          
          for (const comp of filteredComponents) {
            let tooClose = false;
            
            for (const accepted of acceptedComponents) {
              const distance = Math.sqrt(
                Math.pow(comp.centerX - accepted.centerX, 2) + 
                Math.pow(comp.centerY - accepted.centerY, 2)
              );
              
              if (distance < minDistance) {
                tooClose = true;
                break;
              }
            }
            
            if (!tooClose) {
              acceptedComponents.push(comp);
            }
          }
          
          filteredComponents = acceptedComponents;
        }
        
        return filteredComponents.length; // Return the count
      };
      
      // Convert settings to basic parameter object
      const baseParams = {
        threshold: settings.threshold,
        saturationMin: settings.saturationMin,
        minArea: settings.minArea,
        maxArea: settings.maxArea,
        minDistance: settings.minDistance,
        showBounds: settings.showBounds
      };
      
      // Perform parameter sweep with 20 trials (fewer than full robust analysis)
      const countResults = await sweepParameters(baseParams, processWithParams, 20);
      
      // Calculate robust estimate
      const estimate = calculateRobustEstimate(countResults);
      
      // Find the parameter set that gave the closest count to the estimated count
      let bestParamSet: { [key: string]: number | boolean } | null = null;
      let closestDiff = Number.MAX_VALUE;
      
      countResults.forEach(result => {
        const diff = Math.abs(result.count - estimate.estimatedCount);
        if (diff < closestDiff) {
          closestDiff = diff;
          bestParamSet = result.params as { [key: string]: number | boolean };
        }
      });
      
      // Create optimal settings based on the best parameter set
      const optimalSettings: DetectionSettings = {
        threshold: bestParamSet && 'threshold' in bestParamSet ? Number(bestParamSet['threshold']) : settings.threshold,
        saturationMin: bestParamSet && 'saturationMin' in bestParamSet ? Number(bestParamSet['saturationMin']) : settings.saturationMin,
        minArea: bestParamSet && 'minArea' in bestParamSet ? Number(bestParamSet['minArea']) : settings.minArea,
        maxArea: bestParamSet && 'maxArea' in bestParamSet ? Number(bestParamSet['maxArea']) : settings.maxArea,
        minDistance: bestParamSet && 'minDistance' in bestParamSet ? Number(bestParamSet['minDistance']) : settings.minDistance,
        showBounds: settings.showBounds // Keep current showBounds setting
      };
      
      console.log('Optimal settings from robust analysis:', optimalSettings);
      
      // Update robustEstimation with the results but don't show the panel
      setRobustEstimation({
        estimatedCount: estimate.estimatedCount,
        lowerBound: estimate.lowerBound,
        upperBound: estimate.upperBound,
        confidence: estimate.confidence,
        stableRegionSize: estimate.stableRegionSize,
        totalTrials: estimate.totalTrials,
        isProcessing: false,
        progress: 1 // Set progress to 100% when complete
      });
      
      return optimalSettings;
    } catch (error) {
      console.error('Error extracting optimal settings:', error);
      setRobustEstimation(prev => ({
        ...prev,
        isProcessing: false,
        progress: 0
      }));
      return { ...settings }; // Return current settings if error occurs
    }
  }, [settings]);

  // Function to estimate ideal settings based on the uploaded image
  const estimateIdealSettings = (img: HTMLImageElement): DetectionSettings => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      return {
        minArea: 1,
        maxArea: 35,
        threshold: 150,
        saturationMin: 30,
        showBounds: false,
        minDistance: 7
      };
    }
    
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Create black and white mask canvas for analysis
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = img.width;
    maskCanvas.height = img.height;
    const maskCtx = maskCanvas.getContext('2d');
    
    if (!maskCtx) {
      console.error('Could not create mask context');
    }
    
    // Calculate brightness histogram
    const brightnessHistogram = new Array(256).fill(0);
    const saturationHistogram = new Array(256).fill(0);
    let totalBrightPixels = 0;
    
    // Analyze pixels and simultaneously create B&W mask
    const maskData = new Uint8ClampedArray(data.length);
    let isDarkBackground = true; // Assume dark background initially
    
    // First pass: analyze image characteristics
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Calculate HSV
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;
      
      // Calculate brightness value (0-255)
      const brightness = max;
      brightnessHistogram[brightness]++;
      
      // Calculate saturation (0-255)
      const saturation = max === 0 ? 0 : Math.round((delta / max) * 255);
      saturationHistogram[saturation]++;
      
      // Count bright pixels for dot density estimation
      if (brightness > 150) { // Use a lower threshold to catch more potential dots
        totalBrightPixels++;
      }
    }
    
    // Determine if we have a dark background with light dots or vice versa
    let brightCount = 0;
    let darkCount = 0;
    for (let i = 0; i < 100; i++) { // Count dark pixels
      darkCount += brightnessHistogram[i];
    }
    for (let i = 156; i < 256; i++) { // Count bright pixels
      brightCount += brightnessHistogram[i];
    }
    
    // If more bright pixels than dark, we likely have a light background with dark dots
    isDarkBackground = darkCount > brightCount;
    console.log(`Image appears to have a ${isDarkBackground ? 'dark' : 'light'} background:`, { darkCount, brightCount });
    
    // Second pass: create B&W mask based on analysis
    // This helps with more accurate dot detection
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = Math.max(r, g, b);
      
      // Create stark black and white mask based on background type
      if (isDarkBackground) {
        // For dark background: anything brighter than threshold becomes white (255)
        const isBright = brightness > 120;
        maskData[i] = isBright ? 255 : 0;     // R
        maskData[i+1] = isBright ? 255 : 0;   // G
        maskData[i+2] = isBright ? 255 : 0;   // B
        maskData[i+3] = 255;                  // Alpha
      } else {
        // For light background: anything darker than threshold becomes black (0)
        const isDark = brightness < 140;
        maskData[i] = isDark ? 0 : 255;     // R
        maskData[i+1] = isDark ? 0 : 255;   // G
        maskData[i+2] = isDark ? 0 : 255;   // B
        maskData[i+3] = 255;                // Alpha
      }
    }
    
    // Apply mask to the mask canvas for dot detection
    if (maskCtx) {
      const maskImgData = new ImageData(maskData, img.width, img.height);
      maskCtx.putImageData(maskImgData, 0, 0);
      
      // Debug: display mask
      console.log('B&W mask created for dot detection');
      // For debugging, you could append this canvas to the DOM
      // document.body.appendChild(maskCanvas);
    }
    
    // Find brightness threshold (where we have a significant number of bright pixels)
    let thresholdValue = 150; // Default
    let cumulativePixels = 0;
    const totalPixels = img.width * img.height;
    
    // Start from the brightest pixels and go down
    for (let i = 255; i >= 0; i--) {
      cumulativePixels += brightnessHistogram[i];
      // When we reach 1-3% of the image pixels, set threshold
      if (cumulativePixels > totalPixels * 0.01 && cumulativePixels < totalPixels * 0.03) {
        thresholdValue = i;
        break;
      }
    }
    
    // Estimate saturation minimum
    let satMinValue = 30; // Default
    cumulativePixels = 0;
    
    // Calculate average saturation to detect if this is a black and white image
    let totalSaturation = 0;
    for (let i = 0; i < 256; i++) {
      totalSaturation += saturationHistogram[i] * i;
    }
    const avgSaturation = totalSaturation / totalPixels;
    
    // Count pixels with very high brightness (potential dots in B&W images)
    let brightPixelCount = 0;
    for (let i = 220; i <= 255; i++) {
      brightPixelCount += brightnessHistogram[i];
    }
    const brightPixelRatio = brightPixelCount / totalPixels;
    
    // Initialize dot size estimation variables - we'll adjust these later
    let estimatedMinArea = 1; // Default min area
    let estimatedMaxArea = 35; // Default max area
    
    // With our mask approach, we now have a better way to check if this is a B&W image
    // Use both saturation and brightness ratio, but with refined thresholds
    const isBlackAndWhiteImage = avgSaturation < 50 || (isDarkBackground ? brightPixelRatio < 0.15 : darkCount/totalPixels < 0.15);
    
    console.log('Image analysis:', {
      avgSaturation,
      brightPixelRatio,
      isBlackAndWhiteImage
    });
    
    if (isBlackAndWhiteImage) {
      console.log('Black and white image detected, using mask-based parameters');
      // For B&W images, completely disable saturation filtering
      satMinValue = 0;
      
      // Set threshold based on background type from our mask analysis
      if (isDarkBackground) {
        // For dark background with light dots, use lower threshold
        thresholdValue = 100;
        console.log('Using mask-based threshold for dark background B&W image:', thresholdValue);
      } else {
        // For light background with dark dots, use higher threshold but invert detection logic
        thresholdValue = 120;
        console.log('Using mask-based threshold for light background B&W image:', thresholdValue);
      }
      
      // Adjust min/max area for B&W images
      if (brightPixelRatio > 0) {
        // Calculate estimated dot size based on bright pixel ratio
        const avgDotSize = Math.max(3, Math.min(25, Math.floor((brightPixelRatio * totalPixels) / 200)));
        // Update the outer scope variables
        estimatedMinArea = Math.max(1, avgDotSize - 2);
        estimatedMaxArea = Math.max(25, avgDotSize * 2); // More aggressive for B&W
        console.log('Adjusted dot size for B&W image:', { estimatedMinArea, estimatedMaxArea });
      }
    } else {
      // For color images, find saturation value that excludes most low-saturation pixels
      for (let i = 255; i >= 0; i--) {
        cumulativePixels += saturationHistogram[i];
        if (cumulativePixels > totalPixels * 0.05) {
          satMinValue = Math.max(20, i - 20); // Adjust with some margin
          break;
        }
      }
    }
    
    // Estimate optimal dot size range based on image size and bright pixel density
    const pixelRatio = totalBrightPixels / totalPixels;
    
    // These variables are already declared above, we're just potentially adjusting them here
    
    // Adjust max area based on image size and bright pixel density
    if (pixelRatio < 0.005) { // Very sparse dots
      estimatedMaxArea = Math.max(15, Math.floor(totalBrightPixels / 100));
    } else if (pixelRatio > 0.02) { // Dense dots
      estimatedMaxArea = Math.max(25, Math.floor(totalBrightPixels / 200));
    }
    
    console.log('Estimated settings:', {
      threshold: thresholdValue,
      satMinValue,
      minArea: estimatedMinArea,
      maxArea: estimatedMaxArea
    });
    
    // Calculate average brightness to better estimate threshold
    let totalBrightness = 0;
    for (let i = 0; i < 256; i++) {
      totalBrightness += brightnessHistogram[i] * i;
    }
    const avgBrightness = totalBrightness / totalPixels;
    
    // Check if this appears to be a black and white image (already calculated)
    if (isBlackAndWhiteImage) {
      // For B&W images, set brightness threshold based on histogram analysis
      console.log('Analyzing B&W image brightness histogram...');
      
      // First, find the most common bright value (dots)
      let maxBrightVal = 0;
      let maxBrightCount = 0;
      // Lower the starting point to catch less bright white pixels
      for (let i = 150; i <= 255; i++) {
        if (brightnessHistogram[i] > maxBrightCount) {
          maxBrightCount = brightnessHistogram[i];
          maxBrightVal = i;
        }
      }
      
      // Then find the most common dark value (background)
      let maxDarkVal = 0;
      let maxDarkCount = 0;
      // Extend dark range to ensure we capture all possible dark backgrounds
      for (let i = 0; i <= 120; i++) {
        if (brightnessHistogram[i] > maxDarkCount) {
          maxDarkCount = brightnessHistogram[i];
          maxDarkVal = i;
        }
      }
      
      // Calculate the midpoint between the two peaks
      const midPoint = Math.floor((maxBrightVal + maxDarkVal) / 2);
      
      // For B&W images, we need an extremely aggressive threshold approach
      // Set threshold much closer to dark values to ensure we catch all dots
      const contrastLevel = maxBrightVal - maxDarkVal;
      console.log('B&W image contrast level:', contrastLevel);
      
      if (contrastLevel > 120) {
        // High contrast B&W image - use a value very close to dark
        thresholdValue = maxDarkVal + Math.floor((maxBrightVal - maxDarkVal) * 0.2);
      } else if (contrastLevel > 80) {
        // Medium contrast - be more aggressive
        thresholdValue = maxDarkVal + Math.floor((maxBrightVal - maxDarkVal) * 0.25);
      } else {
        // Low contrast - extremely aggressive
        thresholdValue = Math.min(80, maxDarkVal + Math.floor((maxBrightVal - maxDarkVal) * 0.3));
      }
      
      console.log('Black and white image analysis:', {
        maxBrightVal,
        maxDarkVal,
        midPoint,
        thresholdValue
      });
    } else {
      // For color images, adjust threshold based on average brightness
      if (avgBrightness > 180) {
        thresholdValue = Math.max(200, thresholdValue);
      } else if (avgBrightness < 100) {
        thresholdValue = Math.min(120, thresholdValue);
      }
    }
    
    // Estimate minimum distance between dots based on image density
    let estimatedMinDistance = 15; // Default
    
    // Calculate average dot density and adjust distance
    if (pixelRatio < 0.003) {
      // Very sparse dots - use smaller distance
      estimatedMinDistance = 3;
    } else if (pixelRatio > 0.01 && pixelRatio < 0.03) {
      // Medium density - use moderate distance
      estimatedMinDistance = 6;
    } else if (pixelRatio >= 0.03) {
      // High density - use larger distance
      estimatedMinDistance = 10;
    }
    
    // Log the final estimated values
    console.log('Final estimated settings:', {
      threshold: thresholdValue,
      satMinValue,
      minArea: estimatedMinArea,
      maxArea: estimatedMaxArea,
      minDistance: estimatedMinDistance
    });
    
    // Return new settings object
    return {
      minArea: estimatedMinArea,
      maxArea: estimatedMaxArea,
      threshold: thresholdValue,
      saturationMin: satMinValue,
      showBounds: settings.showBounds, // Keep current showBounds setting
      minDistance: estimatedMinDistance
    };
  };

  // Function to cancel the current robust calculation
  const cancelRobustCalculation = useCallback(() => {
    if (workerManagerRef.current) {
      // This will trigger the abort controller in the worker manager
      workerManagerRef.current.terminateWorker();
      workerManagerRef.current = null;
      
      // Reset the robust calculation state
      setRobustEstimation(prev => ({
        ...prev,
        isProcessing: false,
        progress: 0
      }));
      
      console.log('Robust calculation cancelled');
    }
  }, [workerManagerRef]);

  // Function to perform independent robust count estimation without using current slider settings
  const performIndependentRobustAnalysis = useCallback(async (img: HTMLImageElement) => {
    try {
      // Set state to indicate processing started
      setRobustEstimation(prev => ({ 
        ...prev, 
        isProcessing: true,
        progress: 0 
      }));
      
      // Create a temporary canvas to get the image data
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        throw new Error('Could not get canvas context for robust estimation');
      }
      
      // Draw image
      tempCtx.drawImage(img, 0, 0);
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Set default parameters for robust analysis that are independent of slider positions
      // Using median values of parameter ranges to start the search
      const defaultParams = {
        threshold: 160,        // Median of 100-220 range
        saturationMin: 45,    // Median of 20-70 range
        minArea: 3,           // Median of 1-5 range
        maxArea: 25,          // Median of 10-40 range
        minDistance: 7,       // Median of 2-12 range
        showBounds: settings.showBounds // Keep this setting as it's visual only
      };
      
      console.log('Starting independent parameter sweep using Web Worker...');
      
      // Make sure the worker manager exists
      if (!workerManagerRef.current) {
        workerManagerRef.current = createRobustCounterWorkerManager();
      }
      
      // Run the calculation in the worker and show progress updates
      const result = await workerManagerRef.current.sweepParameters(
        imageData,
        defaultParams,
        restrictionPolygon.length >= 3 ? restrictionPolygon : undefined,
        (progress: number) => {
          // Update progress in the UI
          setRobustEstimation(prev => ({
            ...prev,
            progress: progress
          }));
          console.log(`Robust calculation progress: ${Math.round(progress * 100)}%`);
        }
      );
      
      console.log('Web Worker completed parameter sweep');
      console.log('Robust estimation results:', {
        estimatedCount: result.estimatedCount,
        lowerBound: result.lowerBound,
        upperBound: result.upperBound,
        confidence: result.confidence,
        stableRegionSize: result.stableRegionSize,
        totalTrials: result.totalTrials
      });
      
      // Update sliders with optimal parameters if they were returned
      if (result.optimalParams) {
        console.log('Setting optimal parameters from independent analysis:', result.optimalParams);
        setSettings({
          threshold: result.optimalParams?.threshold as number || defaultParams.threshold,
          saturationMin: result.optimalParams?.saturationMin as number || defaultParams.saturationMin,
          minArea: result.optimalParams?.minArea as number || defaultParams.minArea,
          maxArea: result.optimalParams?.maxArea as number || defaultParams.maxArea,
          minDistance: result.optimalParams?.minDistance as number || defaultParams.minDistance,
          showBounds: settings.showBounds
        });
      }
      
      // Update state with results
      setRobustEstimation({
        estimatedCount: result.estimatedCount,
        lowerBound: result.lowerBound,
        upperBound: result.upperBound,
        confidence: result.confidence,
        stableRegionSize: result.stableRegionSize,
        totalTrials: result.totalTrials,
        isProcessing: false,
        progress: 1 // Set to 100%
      });
      
      // Show the robust analysis panel
      setShowRobustAnalysis(true);
      
    } catch (error) {
      console.error('Error in independent robust analysis:', error);
      
      setRobustEstimation(prev => ({
        ...prev,
        isProcessing: false,
        progress: 0
      }));
    }
  }, [workerManagerRef, setRobustEstimation, setSettings, setShowRobustAnalysis, settings.showBounds, restrictionPolygon]);

  // Original robust count estimation - keeps compatibility with existing code
  const performRobustCountEstimation = useCallback(async (img: HTMLImageElement) => {
    try {
      // Set state to indicate processing started
      setRobustEstimation(prev => ({ 
        ...prev, 
        isProcessing: true,
        progress: 0 
      }));
      
      // Create a temporary canvas to get the image data
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        throw new Error('Could not get canvas context for robust estimation');
      }
      
      // Draw image
      tempCtx.drawImage(img, 0, 0);
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Get base parameters
      const baseParams = {
        threshold: settings.threshold,
        saturationMin: settings.saturationMin,
        minArea: settings.minArea,
        maxArea: settings.maxArea,
        minDistance: settings.minDistance,
        showBounds: settings.showBounds
      };
      
      console.log('Starting parameter sweep using Web Worker...');
      
      // Make sure the worker manager exists
      if (!workerManagerRef.current) {
        workerManagerRef.current = createRobustCounterWorkerManager();
      }
      
      // Run the calculation in the worker and show progress updates
      const result = await workerManagerRef.current.sweepParameters(
        imageData,
        baseParams,
        restrictionPolygon.length >= 3 ? restrictionPolygon : undefined,
        (progress: number) => {
          // Update progress in the UI
          setRobustEstimation(prev => ({
            ...prev,
            progress: progress
          }));
          console.log(`Robust calculation progress: ${Math.round(progress * 100)}%`);
        }
      );
      
      console.log('Web Worker completed parameter sweep');
      console.log('Robust estimation results:', {
        estimatedCount: result.estimatedCount,
        lowerBound: result.lowerBound,
        upperBound: result.upperBound,
        confidence: result.confidence,
        stableRegionSize: result.stableRegionSize,
        totalTrials: result.totalTrials
      });
      
      // Update optimal parameters if they were returned
      if (result.optimalParams) {
        console.log('Setting optimal parameters:', result.optimalParams);
        setSettings(prevSettings => ({
          ...prevSettings,
          threshold: result.optimalParams?.threshold as number || prevSettings.threshold,
          saturationMin: result.optimalParams?.saturationMin as number || prevSettings.saturationMin,
          minArea: result.optimalParams?.minArea as number || prevSettings.minArea,
          maxArea: result.optimalParams?.maxArea as number || prevSettings.maxArea,
          minDistance: result.optimalParams?.minDistance as number || prevSettings.minDistance
        }));
      }
      
      // Update state with results
      setRobustEstimation({
        estimatedCount: result.estimatedCount,
        lowerBound: result.lowerBound,
        upperBound: result.upperBound,
        confidence: result.confidence,
        stableRegionSize: result.stableRegionSize,
        totalTrials: result.totalTrials,
        isProcessing: false,
        progress: 1 // Set to 100%
      });
      
      // Show the robust analysis panel
      setShowRobustAnalysis(true);
      
    } catch (error) {
      console.error('Error in robust count estimation:', error);
      
      setRobustEstimation(prev => ({
        ...prev,
        isProcessing: false,
        progress: 0
      }));
    }
  }, [settings, workerManagerRef, setRobustEstimation, setSettings, setShowRobustAnalysis]);

  // Handle robust analysis request
  const handleRobustAnalysis = () => {
    if (image) {
      // Show the robust analysis panel immediately with loading state
      setRobustEstimation(prev => ({
        ...prev,
        isProcessing: true,
        progress: 0
      }));
      setShowRobustAnalysis(true);
      
      // Start the robust count estimation process
      performRobustCountEstimation(image);
    }
  };

  useEffect(() => {
    // Only reprocess if we have an image and we're not currently processing
    if (image && !processing) {
      processImage(image);
    }
  }, [settings, image, processing, processImage]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) {
        console.error('No file selected');
        setResult({ error: 'No file selected' });
        return;
      }
      
      // Clear any existing lasso when uploading a new image
      clearRestrictionArea();
      
      console.log('File selected:', file.name, file.type);
      
      // Create a URL for the selected file
      const fileUrl = URL.createObjectURL(file);
      console.log('File URL created:', fileUrl);
      
      // Create and load the image
      const img = new Image();
      
      img.onload = () => {
        console.log('Image loaded successfully:', img.width, 'x', img.height);
        setImage(img);
        
        // Analyze and estimate ideal settings first
        const newSettings = estimateIdealSettings(img);
        console.log('New estimated settings:', newSettings);
        
        // Apply new settings and then process the image
        setSettings(newSettings);
        
        // Ensure settings are applied before processing
        setTimeout(() => {
          console.log('Processing image with new settings:', newSettings);
          setShowSettings(true);
          processImage(img);
          
          // Automatically run independent robust analysis after processing the image
          // Using independent analysis to ensure it's not dependent on current slider positions
          setTimeout(() => {
            setShowRobustAnalysis(true);
            performIndependentRobustAnalysis(img);
          }, 500);
        }, 100);
      };
      
      img.onerror = (error) => {
        console.error('Error loading image:', error);
        setResult({ error: 'Failed to load image. Please try a different file.' });
      };
      
      console.log('Setting image source to:', fileUrl);
      img.src = fileUrl;
    } catch (error) {
      console.error('Exception in handleImageUpload:', error);
      setResult({ error: 'Error uploading image: ' + (error instanceof Error ? error.message : String(error)) });
    }
  };

  // Reprocess button click handler
  const reprocessImage = () => {
    if (image) {
      // First, get base detection settings from image analysis
      // This decouples the process from current slider positions
      const initialSettings = estimateIdealSettings(image);
      
      // Apply these initial settings
      setSettings(initialSettings);
      
      // Process image with these initial settings
      processImage(image);
      
      // Automatically run robust analysis after reprocessing the image
      // with independent parameter search
      setTimeout(() => {
        setShowRobustAnalysis(true);
        performIndependentRobustAnalysis(image);
      }, 500);
    }
  };
  
  // Reset all state and file input
  const resetAll = () => {
    setImage(null);
    setResult(null);
    setProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="dot-counter-container">
      <div className="dot-counter-content">
        {/* Header */}
        <div className="dot-counter-header">
          <h1 className="dot-counter-title">
            Drone Light Show Dot Counter
          </h1>
          <p className="dot-counter-description">
            Upload an image of drone light displays to automatically count the dots
          </p>
        </div>

        {/* Controls */}
        <div className="dot-counter-controls">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="dot-counter-file-input"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="dot-counter-button upload-button"
            aria-label="Upload Image"
          >
            <Upload size={20} />
            <span>Upload Image</span>
          </button>
          
          {image && (
            <>
              <button
                onClick={reprocessImage}
                disabled={processing}
                className={`dot-counter-button reprocess-button ${processing ? 'disabled' : ''}`}
                aria-label="Reprocess Image"
                aria-disabled={processing}
              >
                <Camera size={20} />
                <span>{processing ? 'Processing...' : 'Reprocess'}</span>
              </button>
              
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="dot-counter-button settings-button"
                aria-label="Settings"
                aria-expanded={showSettings}
              >
                <Settings size={20} />
                <span>Settings</span>
              </button>
              
              <button
                onClick={resetAll}
                className="dot-counter-button reset-button"
                aria-label="Reset"
              >
                <RotateCcw size={20} />
                <span>Reset</span>
              </button>
            </>
          )}
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="dot-counter-settings-panel" role="region" aria-label="Detection Settings">
            <h3 className="settings-title">Detection Settings</h3>
            
            <div className="settings-controls">
              <div className="settings-control">
                <label htmlFor="min-area" className="settings-label">Min Dot Area (pixels)</label>
                <input
                  id="min-area"
                  type="range"
                  min="1"
                  max="50"
                  value={settings.minArea}
                  onChange={(e) => setSettings({...settings, minArea: parseInt(e.target.value)})}
                  className="settings-range"
                  aria-valuemin={1}
                  aria-valuemax={50}
                  aria-valuenow={settings.minArea}
                />
                <span className="settings-value">{settings.minArea}</span>
              </div>
              
              <div className="settings-control">
                <label htmlFor="max-area" className="settings-label">Max Dot Area (pixels)</label>
                <input
                  id="max-area"
                  type="range"
                  min="20"
                  max="100"
                  value={settings.maxArea}
                  onChange={(e) => setSettings({...settings, maxArea: parseInt(e.target.value)})}
                  className="settings-range"
                  aria-valuemin={20}
                  aria-valuemax={100}
                  aria-valuenow={settings.maxArea}
                />
                <span className="settings-value">{settings.maxArea}</span>
              </div>
              
              <div className="settings-control">
                <label htmlFor="brightness" className="settings-label">Brightness Threshold</label>
                <input
                  id="brightness"
                  type="range"
                  min="50"
                  max="255"
                  value={settings.threshold}
                  onChange={(e) => setSettings({...settings, threshold: parseInt(e.target.value)})}
                  className="settings-range"
                  aria-valuemin={50}
                  aria-valuemax={255}
                  aria-valuenow={settings.threshold}
                />
                <span className="settings-value">{settings.threshold}</span>
              </div>
              
              <div className="settings-control">
                <label htmlFor="saturation" className="settings-label">Min Saturation</label>
                <input
                  id="saturation"
                  type="range"
                  min="0"
                  max="255"
                  value={settings.saturationMin}
                  onChange={(e) => setSettings({...settings, saturationMin: parseInt(e.target.value)})}
                  className="settings-range"
                  aria-valuemin={0}
                  aria-valuemax={255}
                  aria-valuenow={settings.saturationMin}
                />
                <span className="settings-value">{settings.saturationMin}</span>
              </div>
              
              <div className="settings-control">
                <label htmlFor="min-distance" className="settings-label">Min Distance Between Dots</label>
                <input
                  id="min-distance"
                  type="range"
                  min="0"
                  max="20"
                  value={settings.minDistance}
                  onChange={(e) => setSettings({...settings, minDistance: parseInt(e.target.value)})}
                  className="settings-range"
                  aria-valuemin={0}
                  aria-valuemax={20}
                  aria-valuenow={settings.minDistance}
                />
                <span className="settings-value">{settings.minDistance}px</span>
              </div>
              

            </div>
          </div>
        )}

        {/* Results */}
        <div className="dot-counter-results">
          {/* Image Display */}
          {image && (
            <div className="image-display" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 className="section-title" style={{ margin: 0 }}>Processed Image</h3>
                
                {/* Lasso Control Button - Aligned with heading */}
                <div>
                  {!isDrawingRestriction && restrictionPolygon.length === 0 && (
                    <button
                      onClick={startDrawingRestriction}
                      className="dot-counter-button area-restriction-button"
                      disabled={processing}
                    >
                      <span>Draw Lasso Area</span>
                    </button>
                  )}
                  
                  {isDrawingRestriction && (
                    <button
                      onClick={() => {
                        setIsDrawingRestriction(false);
                        setShowRestrictionArea(true);
                      }}
                      className="dot-counter-button area-restriction-button finish"
                      disabled={restrictionPolygon.length < 3}
                    >
                      <span>Finish Lasso</span>
                    </button>
                  )}
                  
                  {(showRestrictionArea || restrictionPolygon.length > 0) && !isDrawingRestriction && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={startDrawingRestriction}
                        className="dot-counter-button area-restriction-button modify"
                        disabled={processing}
                      >
                        <span>Redraw Lasso</span>
                      </button>
                      
                      <button
                        onClick={clearRestrictionArea}
                        className="dot-counter-button area-restriction-button clear"
                        disabled={processing}
                      >
                        <span>Clear Restriction</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Drawing Instructions */}
              {isDrawingRestriction && (
                <div className="drawing-instructions" style={{ position: 'absolute', top: '60px', left: '16px', zIndex: 10, backgroundColor: 'rgba(0,0,0,0.8)', color: 'white', padding: '8px 12px', borderRadius: '4px', fontSize: '14px' }}>
                  <p style={{ margin: 0 }}>Hold and drag to draw your restriction area. Release to finish.</p>
                </div>
              )}
              

              
              <div className="canvas-container">
                <canvas
                  ref={canvasRef}
                  className="result-canvas"
                  aria-label="Processed image canvas"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: isDrawingRestriction ? 'crosshair' : 'default' }}
                />
                {processing && (
                  <div className="processing-overlay" aria-live="polite">
                    <div className="processing-indicator">
                      <div className="spinner"></div>
                      <p>Processing image...</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="image-display-controls">
                <div className="image-display-checkbox">
                  <input
                    id="show-bounds"
                    type="checkbox"
                    checked={settings.showBounds}
                    onChange={(e) => setSettings({...settings, showBounds: e.target.checked})}
                    className="settings-checkbox-input"
                  />
                  <label htmlFor="show-bounds" className="settings-checkbox-label">Show detection bounds</label>
                </div>
              </div>
            </div>
          )}

          {/* Results Panel */}
          {result && (
            <div className="results-panel">
              <h3 className="section-title">Detection Results</h3>
              
              {result.error ? (
                <div className="error-message" role="alert">
                  <Info className="error-icon" size={16} />
                  {result.error}
                </div>
              ) : (
                <div className="results-content">
                  <div className="results-grid">
                    <div className="result-stat valid-dots">
                      <div className="stat-value">{result.validComponents}</div>
                      <div className="stat-label">Valid Dots</div>
                    </div>
                    
                    <div className="result-stat total-components">
                      <div className="stat-value">{result.totalComponents}</div>
                      <div className="stat-label">Total Components</div>
                    </div>
                  </div>
                  
                  {/* Robust Analysis Button or Results */}
                  {!robustEstimation.isProcessing && !showRobustAnalysis && (
                    <div className="action-buttons">
                      <button 
                        className="action-button robust-analysis-btn"
                        onClick={handleRobustAnalysis}
                        title="Perform robust count analysis with parameter sweep"
                        disabled={processing || robustEstimation.isProcessing}
                      >
                        <BarChart2 size={16} />
                        <span>Robust Count Estimation</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Robust Analysis Results */}
                  {showRobustAnalysis && !robustEstimation.isProcessing && (
                    <div className="result-stat robust-estimate">
                      <h4 className="section-title">Robust Dot Count Analysis</h4>
                      <div className="estimate-value">{Math.round(robustEstimation.estimatedCount)}</div>
                      <div className="stat-label">Estimated Count</div>
                      
                      <div className="robust-stats-details">
                        <div className="detail-row">
                          <span 
                            className="detail-label" 
                            title="The minimum and maximum estimated dot counts across all parameter combinations tested"
                          >
                            Range:
                          </span>
                          <span className="detail-value">
                            {Math.round(robustEstimation.lowerBound)} - {Math.round(robustEstimation.upperBound)}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span 
                            className="detail-label" 
                            title="Statistical confidence level of the estimation based on consistency across different parameter settings"
                          >
                            Confidence:
                          </span>
                          <div className="confidence-container">
                            <span className="detail-value">{(robustEstimation.confidence * 100).toFixed(0)}%</span>
                            <div className="confidence-bar large">
                              <div 
                                className="confidence-level" 
                                style={{ width: `${robustEstimation.confidence * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="detail-row">
                          <span 
                            className="detail-label" 
                            title="Number of parameter combinations that produced consistent results out of total trials performed"
                          >
                            Stability:
                          </span>
                          <span className="detail-value">
                            {robustEstimation.stableRegionSize} / {robustEstimation.totalTrials} trials
                          </span>
                        </div>
                      </div>
                      
                      <div className="analysis-note">
                        <Info size={16} />
                        <span>Calculated by sweeping parameters to find the largest stable region.</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Robust Analysis Loading Overlay */}
                  {robustEstimation.isProcessing && (
                    <div className="robust-analysis-loading">
                      <div className="loading-message">
                        <div className="spinner"></div>
                        <p>Analyzing with parameter sweep...</p>
                        <p className="small-text">This may take a few moments</p>
                        <div className="progress-container">
                          <div className="progress-label">
                            <span>Progress</span>
                            <span>{robustEstimation.progress}%</span>
                          </div>
                          <div className="progress-bar" style={{ width: `${robustEstimation.progress}%` }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="result-details">
                    <div className="detail-row">
                      <span className="detail-label">Image Size:</span>
                      <span className="detail-value">{result.imageSize?.width} × {result.imageSize?.height}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Bright Pixels:</span>
                      <span className="detail-value">{result.brightPixels?.toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Area Range:</span>
                      <span className="detail-value">{settings.minArea} - {settings.maxArea} px</span>
                    </div>
                    
                    {result.filteredComponents && result.filteredComponents.length > 0 && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Avg Area:</span>
                          <span className="detail-value">{Math.round(result.filteredComponents.reduce((sum, c) => sum + c.area, 0) / result.filteredComponents.length)} px</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Size Range:</span>
                          <span className="detail-value">{Math.min(...result.filteredComponents.map(c => c.area))} - {Math.max(...result.filteredComponents.map(c => c.area))} px</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DotCounter;
